/**
 * 중개사 전문가 프로필 페이지 (/agent/:agentId)
 * 공개 페이지 — 로그인 없이 접근 가능
 * 본인(로그인 중개사)이면 편집 버튼 노출
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { apiFetch } from "../../utils/api";
import { formatPrice } from "../../utils/format";

const API_BASE = import.meta.env.VITE_API_URL || "";

const SPECIALTY_OPTIONS = ["농지", "임야", "묘터", "상가", "주택", "공장", "창고", "대지"];
const REGION_OPTIONS = [
  "서울", "경기", "인천", "강원", "충북", "충남", "대전", "세종",
  "경북", "경남", "대구", "울산", "부산", "전북", "전남", "광주", "제주",
];

const TYPE_COLORS: Record<string, string> = {
  "농지": "#16A34A", "임야": "#92400E", "묘터": "#7C3AED",
  "공장": "#6B7280", "창고": "#EA580C", "상가": "#3B82F6", "주택": "#2563EB",
};

interface AgentData {
  id: string;
  name: string;
  office_name: string;
  region_sido: string;
  region_sigungu: string;
  is_verified: boolean;
  trust_score: number;
  total_listings: number;
  total_deals_closed: number;
  created_at: string;
  specialty: string[];
  regions: string[];
  bio: string;
  listings: any[];
}

export function AgentProfilePage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const m = useIsMobile();

  const [agent, setAgent] = useState<AgentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // 편집 폼
  const [editSpecialty, setEditSpecialty] = useState<string[]>([]);
  const [editRegions, setEditRegions] = useState<string[]>([]);
  const [editBio, setEditBio] = useState("");

  // 본인 여부 확인
  const brokerInfo = (() => {
    try { return JSON.parse(localStorage.getItem("broker_info") || "null"); } catch { return null; }
  })();
  const brokerToken = localStorage.getItem("broker_token");
  const isOwn = !!brokerToken && !!brokerInfo && brokerInfo.id === agentId;

  usePageTitle(agent ? `${agent.name} 중개사 프로필` : "중개사 프로필");

  useEffect(() => {
    if (!agentId) return;
    fetch(`${API_BASE}/api/agents/${agentId}`)
      .then((r) => {
        if (!r.ok) { setNotFound(true); return null; }
        return r.json();
      })
      .then((data: AgentData | null) => {
        if (!data) return;
        setAgent(data);
        setEditSpecialty(data.specialty || []);
        setEditRegions(data.regions || []);
        setEditBio(data.bio || "");
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [agentId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/agents/profile", {
        method: "PATCH",
        body: JSON.stringify({ specialty: editSpecialty, regions: editRegions, bio: editBio }),
      });
      setAgent((prev) => prev ? { ...prev, specialty: editSpecialty, regions: editRegions, bio: editBio } : prev);
      setEditing(false);
    } catch (e: any) {
      alert(e.message || "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    const url = `https://land.genomic.cc/agent/${agentId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleKakaoShare = () => {
    if (!agent) return;
    const url = `https://land.genomic.cc/agent/${agentId}`;
    const w = window as any;
    if (w.Kakao?.Share?.sendDefault) {
      w.Kakao.Share.sendDefault({
        objectType: "feed",
        content: {
          title: `${agent.name} · ${agent.office_name}`,
          description: agent.bio || `${agent.region_sido} ${agent.region_sigungu} 토지 전문 중개사`,
          imageUrl: "https://land.genomic.cc/og-image.png",
          link: { mobileWebUrl: url, webUrl: url },
        },
        buttons: [{ title: "프로필 보기", link: { mobileWebUrl: url, webUrl: url } }],
      });
    } else {
      handleCopyLink();
    }
  };

  const toggleEditTag = (list: string[], setList: (v: string[]) => void, val: string) => {
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  };

  if (loading) return (
    <div style={st.page}><AppHeader />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "var(--text-muted)" }}>불러오는 중...</p>
      </div>
      <AppFooter />
    </div>
  );

  if (notFound || !agent) return (
    <div style={st.page}><AppHeader />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "center", gap: 16, padding: 40 }}>
        <p style={{ fontSize: 48 }}>{"🔍"}</p>
        <p style={{ color: "var(--text-muted)", fontSize: 15 }}>중개사를 찾을 수 없습니다</p>
        <button onClick={() => navigate(-1)} style={st.btn}>뒤로가기</button>
      </div>
      <AppFooter />
    </div>
  );

  const joinYear = agent.created_at ? new Date(agent.created_at).getFullYear() : null;

  return (
    <div style={st.page}>
      <AppHeader />
      <div style={{ ...st.container, padding: m ? "20px 16px 48px" : "40px 32px 72px" }}>

        {/* ── 프로필 헤더 카드 ── */}
        <div style={{ ...st.card, display: "flex", flexDirection: m ? "column" as const : "row", gap: m ? 20 : 32, alignItems: m ? "flex-start" : "center", marginBottom: m ? 20 : 28 }}>

          {/* 아바타 */}
          <div style={{ flexShrink: 0, width: m ? 64 : 80, height: m ? 64 : 80, borderRadius: "50%", background: "linear-gradient(135deg,#B8972E,#D4AF37)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: m ? 26 : 32, color: "#0F172A", fontWeight: 800 }}>
            {agent.name.charAt(0)}
          </div>

          {/* 이름/소속/배지 */}
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const, marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: m ? 22 : 28, fontWeight: 800, color: "var(--text-primary)" }}>{agent.name}</h1>
              {agent.is_verified && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "rgba(212,168,83,0.15)", color: "#D4A853", border: "1px solid rgba(212,168,83,0.3)" }}>
                  {"✓"} 인증 중개사
                </span>
              )}
            </div>
            <p style={{ margin: "0 0 8px", fontSize: 15, color: "var(--text-secondary)" }}>{agent.office_name}</p>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              {agent.region_sido} {agent.region_sigungu}
              {joinYear && <span style={{ marginLeft: 10 }}>가입 {joinYear}년</span>}
            </p>
          </div>

          {/* 공유 + 편집 버튼 */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
            <button onClick={handleCopyLink} style={{ ...st.outlineBtn, minWidth: 90 }}>
              {copied ? "✓ 복사됨" : "\uD83D\uDD17 링크 복사"}
            </button>
            <button onClick={handleKakaoShare} style={{ ...st.outlineBtn, background: "#FEE500", color: "#3A1D1D", border: "1px solid #FEE500" }}>
              {"\uD83D\uDCAC"} 카카오
            </button>
            {isOwn && !editing && (
              <button onClick={() => setEditing(true)} style={st.btn}>{"✏️"} 편집</button>
            )}
          </div>
        </div>

        {/* ── 편집 모드 ── */}
        {editing && (
          <div style={{ ...st.card, marginBottom: m ? 20 : 28, borderColor: "rgba(212,168,83,0.4)" }}>
            <h3 style={st.sectionTitle}>프로필 편집</h3>

            <div style={{ marginBottom: 20 }}>
              <p style={st.fieldLabel}>한줄 소개 (최대 200자)</p>
              <textarea
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                maxLength={200}
                rows={3}
                placeholder="예: 경북 지역 10년 경력 토지 전문 중개사입니다."
                style={{ ...st.input, resize: "vertical" as const }}
              />
              <p style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "right" as const, marginTop: 4 }}>{editBio.length}/200</p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <p style={st.fieldLabel}>전문 매물 유형 (다중 선택)</p>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {SPECIALTY_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleEditTag(editSpecialty, setEditSpecialty, s)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, border: "1px solid",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      borderColor: editSpecialty.includes(s) ? "#D4A853" : "var(--border-medium)",
                      background: editSpecialty.includes(s) ? "rgba(212,168,83,0.15)" : "var(--bg-secondary)",
                      color: editSpecialty.includes(s) ? "#D4A853" : "var(--text-muted)",
                    }}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <p style={st.fieldLabel}>전문 지역 (다중 선택)</p>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {REGION_OPTIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleEditTag(editRegions, setEditRegions, r)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, border: "1px solid",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      borderColor: editRegions.includes(r) ? "#D4A853" : "var(--border-medium)",
                      background: editRegions.includes(r) ? "rgba(212,168,83,0.15)" : "var(--bg-secondary)",
                      color: editRegions.includes(r) ? "#D4A853" : "var(--text-muted)",
                    }}
                  >{r}</button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setEditing(false)} style={st.outlineBtn}>취소</button>
              <button onClick={handleSave} disabled={saving} style={{ ...st.btn, opacity: saving ? 0.6 : 1 }}>
                {saving ? "저장 중..." : "저장하기"}
              </button>
            </div>
          </div>
        )}

        {/* ── 통계 카드 4종 ── */}
        <div style={{ display: "grid", gridTemplateColumns: m ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: m ? 10 : 14, marginBottom: m ? 20 : 28 }}>
          {[
            { label: "신뢰도", value: `${agent.trust_score}점`, color: "#D4A853" },
            { label: "등록 매물", value: `${agent.total_listings}건` },
            { label: "거래 완료", value: `${agent.total_deals_closed}건`, color: "#10B981" },
            { label: "활성 매물", value: `${agent.listings.length}건`, color: "#3B82F6" },
          ].map((s) => (
            <div key={s.label} style={{ background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-lg)", padding: m ? "14px" : "18px 20px", textAlign: "center" as const }}>
              <p style={{ margin: "0 0 4px", fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>{s.label}</p>
              <p style={{ margin: 0, fontSize: m ? 20 : 24, fontWeight: 800, color: s.color || "var(--text-primary)" }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* ── 두 컬럼 레이아웃 (데스크탑) ── */}
        <div style={{ display: m ? "block" : "grid", gridTemplateColumns: "1fr 2fr", gap: 28 }}>

          {/* 왼쪽: 소개/전문분야/지역 */}
          <div style={{ display: "flex", flexDirection: "column" as const, gap: m ? 16 : 20, marginBottom: m ? 20 : 0 }}>

            {/* 한줄 소개 */}
            <div style={st.card}>
              <h3 style={st.sectionTitle}>{"💬"} 소개</h3>
              {agent.bio ? (
                <p style={{ margin: 0, fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7 }}>{agent.bio}</p>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
                  {isOwn ? "소개를 입력해 보세요 (편집 버튼 클릭)" : "소개 정보 없음"}
                </p>
              )}
            </div>

            {/* 전문 매물 유형 */}
            <div style={st.card}>
              <h3 style={st.sectionTitle}>{"🏷️"} 전문 매물 유형</h3>
              {agent.specialty.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                  {agent.specialty.map((s) => (
                    <span key={s} style={{ padding: "5px 13px", borderRadius: 20, fontSize: 13, fontWeight: 600, background: `${TYPE_COLORS[s] || "#6B7280"}22`, color: TYPE_COLORS[s] || "#94A3B8", border: `1px solid ${TYPE_COLORS[s] || "#6B7280"}44` }}>
                      {s}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
                  {isOwn ? "전문 유형을 선택해 보세요" : "정보 없음"}
                </p>
              )}
            </div>

            {/* 전문 지역 */}
            <div style={st.card}>
              <h3 style={st.sectionTitle}>{"📍"} 전문 지역</h3>
              {agent.regions.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                  {agent.regions.map((r) => (
                    <span key={r} style={{ padding: "5px 13px", borderRadius: 20, fontSize: 13, fontWeight: 600, background: "rgba(212,168,83,0.1)", color: "#D4A853", border: "1px solid rgba(212,168,83,0.25)" }}>
                      {r}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>
                  {isOwn ? "전문 지역을 선택해 보세요" : "정보 없음"}
                </p>
              )}
            </div>

          </div>

          {/* 오른쪽: 현재 등록 매물 */}
          <div>
            <h2 style={{ ...st.sectionTitle, fontSize: 15, marginBottom: 16 }}>
              {"📋"} 현재 등록 매물 ({agent.listings.length}건)
            </h2>
            {agent.listings.length === 0 ? (
              <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-xl)", padding: "40px 20px", textAlign: "center" as const }}>
                <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>현재 등록된 활성 매물이 없습니다</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 12 }}>
                {agent.listings.map((l: any) => (
                  <Link
                    key={l.id}
                    to={`/listings/${l.id}`}
                    style={{ textDecoration: "none", display: "block" }}
                  >
                    <div style={{
                      background: "var(--bg-card)",
                      border: "1px solid var(--border-light)",
                      borderLeft: `3px solid ${TYPE_COLORS[l.property_type] || "#6B7280"}`,
                      borderRadius: "var(--radius-xl)",
                      padding: m ? "14px" : "16px 20px",
                      transition: "var(--transition-fast)",
                    }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(212,168,83,0.4)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-light)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap" as const, gap: 8 }}>
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: `${TYPE_COLORS[l.property_type] || "#6B7280"}22`, color: TYPE_COLORS[l.property_type] || "#94A3B8", marginRight: 8 }}>
                            {l.property_type}
                          </span>
                          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                            {l.region_sido} {l.region_sigungu}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#FBBF24" }}>
                          {formatPrice(l.price_manwon)}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: 16 }}>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          면적: <strong style={{ color: "var(--text-secondary)" }}>{l.area_pyeong?.toLocaleString()}평</strong>
                        </span>
                        {l.land_category && (
                          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                            지목: <strong style={{ color: "var(--text-secondary)" }}>{l.land_category}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* 이 중개사에게 문의하기 */}
            <div style={{ ...st.card, marginTop: m ? 20 : 24, textAlign: "center" as const }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
                {"✉️"} 이 중개사에게 직접 문의하기
              </p>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text-muted)" }}>
                매물 목록에서 원하는 매물을 선택 후 문의할 수 있습니다
              </p>
              <button
                onClick={() => navigate(`/listings?broker_id=${agentId}`)}
                style={st.btn}
              >
                이 중개사의 매물 보기
              </button>
            </div>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--bg-primary)",
  },
  container: {
    flex: 1,
    maxWidth: 1100,
    margin: "0 auto",
    width: "100%",
  },
  card: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-xl)",
    padding: "20px 24px",
    boxShadow: "var(--shadow-sm)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--lt-gold)",
    margin: "0 0 14px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
  },
  btn: {
    padding: "10px 22px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "linear-gradient(135deg,#B8972E,#D4AF37)",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  outlineBtn: {
    padding: "10px 16px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-card)",
    color: "var(--text-secondary)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-secondary)",
    margin: "0 0 8px",
  },
  input: {
    padding: "10px 14px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
    lineHeight: 1.6,
  },
};
